/**
 * Noibu Feature Flag — cart reassurance experiment ("cart-reassurance").
 *
 * Structurally parallel to assets/noibu-feature-flag.js (the home-page
 * experiment). It is a separate file on purpose: that file is currently the
 * control-defining code for a live experiment, and editing it to add a second
 * flag would change the control mid-flight. Two small files beat one clever
 * one here.
 *
 * Both experiences are server-rendered by Liquid. This script does not build
 * DOM; it sets a class on <html> that decides whether the already-rendered
 * reassurance block is visible. A flag failure is therefore a no-op and the
 * cart renders exactly as it does today.
 *
 *   current-experience  -> html.nff-cart--a  (control, the default)
 *   show-reassurance    -> html.nff-cart--b
 *
 * PREREQUISITE — READ THIS BEFORE ASSUMING THE TEST IS RUNNING
 * NOIBUJS_CONFIG.feature_flag_key must be provisioned for this store. Without
 * it, collect-core never injects collect-ff.js, window.NoibuFeatureFlag is
 * never defined, and no visitor is ever bucketed. That is exactly what
 * happened to the home-page experiment between 2026-08-21 and 2026-08-26: it
 * recorded zero exposures while the page looked perfectly correct, because
 * control is what the server already renders. reportState() below exists so
 * that failure mode is visible in Explorations instead of silent.
 */
(function () {
  'use strict';

  var FLAG = '2083-cart-reassurance';
  var CONTROL = 'current-experience';
  var VARIATION_B = 'show-reassurance';
  var CACHE_KEY = 'nff.cart-reassurance';

  var REPORT_KEY = 'nff_cart-reassurance';
  var STATE_ATTR = 'data-nff-cart-state';

  // Anchored to window.load because the client provably cannot arrive before
  // it — collect-core is injected by a ScriptTag on load, and collect-ff by
  // collect-core after that.
  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-cart--a';
  CLASSES[VARIATION_B] = 'nff-cart--b';

  var root = document.documentElement;

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }

    // NOIBUJS.addCustomAttribute comes from collect-core, which loads
    // independently of collect-ff — so reporting works in precisely the case
    // where the flags do not.
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // Not terminal — collect-core may still be booting. Retrying beats
        // dropping the exposure, which would bias the arm rather than break it.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load — is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (root.classList.contains(className)) return;

    root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
    root.classList.add(className);
    root.setAttribute('data-nff-cart', variation);

    document.dispatchEvent(
      new CustomEvent('noibu:cart-variation', { detail: { variation: variation } })
    );
  }

  function remember(variation) {
    // Cached so a repeat collector — this store's primary customer — does not
    // watch the checkout button jump on arrival. The SDK stays the source of
    // truth; this only feeds the paint-time hint in the gate section. Wrapped
    // because storage throws in private mode and some embedded webviews.
    try {
      window.localStorage.setItem(CACHE_KEY, variation);
    } catch (error) {
      /* no-op */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_cart=show-reassurance. Lets this be reviewed on a
    // preview theme before the flag exists in the Noibu console.
    try {
      var value = new URLSearchParams(window.location.search).get('nff_cart');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable — fall through to the flag. */
    }
    return null;
  }

  function render(client) {
    var variation = client.getStringValue(FLAG, CONTROL);

    if (variation === VARIATION_B) {
      activate(VARIATION_B);
      remember(VARIATION_B);
      return VARIATION_B;
    }

    // Control, visitors excluded by targeting, and visitors whose flags never
    // resolved all land here.
    activate(CONTROL);
    remember(CONTROL);
    return CONTROL;
  }

  var forced = forcedVariation();
  if (forced) {
    // Deliberately skips remember() and never touches the SDK, so a forced
    // preview neither poisons the visitor's cache nor records an exposure that
    // would skew the experiment's own numbers.
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));

      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      // Do NOT remember() here. Caching a value the SDK never produced would
      // make the paint-time script replay a decision that was never made, and
      // would hide the failure on every subsequent page view.
      if (!root.hasAttribute('data-nff-cart')) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
